import React, { useEffect, useState } from 'react';
import ViewWrapperWithCommandBar from '../../../Layout/ViewWrapperWithCommandBar';
import Wizard from '../../../Wizard/Wizard';
import { DefaultPalette, ICommandBarItemProps, ISelectableOption, Text } from '@fluentui/react';
import { titleCommandBarButtonStyles, defaultCommandBarButtonStyles } from '../../../../styles/button-styles';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../common/constants';
import { DateTime } from 'luxon';
import TimeTaxSupportSystemClient from '../../../../system';
import MessageCourier from '../../../../lib/MessageCourier';
import { useTranslation } from 'react-i18next';
import SettlementPeriod from './Wizard/SettlementPeriod';
import { SettlementsTable } from './Wizard/SettlementsTable';
import GenerateSettlement from './Wizard/GenerateSettlement';
import Loading from '../../../Spinners/Loading';
import { User } from '../../../../system/User';
import { LiquidacionInsumo } from '../../../../system/settlement/aje/liquidacion-insumos';
import { AgrupadorCorporacionLiquidadora, AgrupadorCorporacionLiquidadoraTipo } from '../../../../system/agrupador_corporacion_liquidadora/AgrupadorCorporacionLiquidadoraSystem';
import useAgrupadorCorporacionLiquidadoraList from '../../../../shared/hooks/useAgrupadorCorporacionLiquidadoraList';
import { LiquidacionAsincronaV2, LiquidacionAsincronaV2CreatePayload } from '../../../../system/liquidacion_asincrona/LiquidacionAsincronaV2System';
import { TipoInsumos } from '../../carga/enums/tipo_insumos.enum';

interface ISettlementsViewAjeProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: any;
  user: User;
}

export const DISTRIBUCION_PROYECTOS = 'P';
export const DISTRIBUCION_ACTIVIDADES = 'A';
export const DISTRIBUCION_HIBRIDA = 'H';

const DirectSettlementsViewAje = (props: ISettlementsViewAjeProps) => {
  const { t } = useTranslation();
  const [canGoNext, setCanGoNext] = useState(false);

  const [month, setMonth] = useState<DateTime>(DateTime.now());

  const [liquidacionInsumosList, setLiquidacionInsumosList] = useState<LiquidacionInsumo[]>([]);

  const [generatedSettlementResponse, setGeneratedSettlementResponse] = useState<LiquidacionAsincronaV2>();

  const [lastConfirmatedSettlement, setLastConfirmatedSettlement] = useState<String>();

  const [settlementType, setSettlementType] = useState<ISelectableOption<AgrupadorCorporacionLiquidadora>>({ key: '', text: '' });
  const agrupadoresCorporacionLiquidadora = useAgrupadorCorporacionLiquidadoraList(props.system);

  const navigate = useNavigate();

  useEffect(() => {
      if(month && settlementType && settlementType.key){
        getPreliquidacionReport();
      }
  }, [month, settlementType]);

  useEffect(() => {
    setLiquidacionInsumosList([]);
  }, [settlementType]);

  const generateSettlement = async () => {
    if (month) {
      props.changeBlockUI(true);
      try {
        const insumoSubDiario = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.SUB_DIARIO);
        if (!insumoSubDiario) {
          return props.courier.messageError('No se encontro el insumo sub diario para generar la liquidación');
        }

        const periodo = month.toFormat('yyyyLL');

        if(!settlementType || !settlementType.data){
          props.courier.messageError(t('Debe seleccionar un tipo de liquidación antes de continuar'));
          return;
        }

        // Validar que el tipo de liquidación seleccionado sea compatible para ser generado como directa (AJE_FILMS)
        if (settlementType.data.agrcorliq_tipo === AgrupadorCorporacionLiquidadoraTipo.AJE_FILMS) {
          const payload: LiquidacionAsincronaV2CreatePayload = {
            liquidacion: {
              lidasinc_agrcorliqid: settlementType.key,
              lidasinc_solicitud_colid: props.user.getCollaboratorId(),
              lidasinc_periodo: periodo,
            },
            detalle: {
              liqasincdet_data: {
                landing: [
                  {
                    ajeFilms: insumoSubDiario.liqins_path,
                  },
                ],
              },
            },
          };
          const response = await props.system.getLiquidacionAsincronaV2System().create(payload);
  
          if (response && Object.keys(response.data).length > 1) {
            setGeneratedSettlementResponse(response.data);
            props.courier.messageSuccess(t('Se iniciará con el proceso de liquidación'));
            setButtonDisability(false);
          }
        } else {
          props.courier.messageError(t('El tipo de liquidación seleccionado no es compatible para ser generado'));
        }

      } catch (error) {
        props.courier.messageError(error);
      } finally {
        props.changeBlockUI(false);
      }
    }
  };

  const getPreliquidacionReport = async () => {
    if (month) {
      if (!settlementType || !settlementType.key) {
        props.courier.messageError(t('Debe seleccionar un tipo de liquidación antes de continuar'));
        return;
      }

      props.changeBlockUI(true);
      props.courier.messageError('');
      try {
        setLiquidacionInsumosList([]);

        const periodo = month.toFormat('yyyyLL');
        const periodoHyphenated = month.toFormat('yyyy-LL');

        const existsResponse = await props.system.getLiquidacionAsincronaV2System().existsByPeriodo({
          lidasinc_periodo: periodo,
          lidasinc_agrcorliqid: settlementType.key
        });

        if (existsResponse.data.existe) {
          props.courier.messageError('Ya existe una liquidación generada para el período seleccionado');
          return;
        }


        const data = await props.system.getSettlementSystem().getInsumosByPeriodoAndAgrupador(periodoHyphenated, settlementType.key);

        setLiquidacionInsumosList(data);

        if (data.length === 0) {
          props.courier.messageError('No se encontraron insumos para ese periodo y grupo seleccionado');
        } else {
          props.courier.messageError('');
        }
      } catch (error: any) {
        props.courier.messageError(error?.message || error);
      } finally {
        props.changeBlockUI(false);
      }
    }
  };

  const periodString = () => {
    return month.toFormat('LL/yyyy');
  };

  const Titles = [t('1. Período'), t('2. Validación de insumos'), t('3. Generar liquidación')];
  const Descriptions = [
    t('Elije el periodo y el tipo de liquidación correspondientes para generar la próxima liquidación directa.'),

    <div className="ms-Grid-row" style={{ maxWidth: 400 }}>
      <div className="ms-Grid-col ms-lg12 ms-sm12 mt-1">
        <div className="tts-liquidacion-info">
          <div className="tts-liquidacion-item">
            <span className="tts-liquidacion-label">{t('Periodo a liquidar')}:</span>
            <span className="tts-liquidacion-value">{periodString()}</span>
          </div>

          <div className="tts-liquidacion-item">
            <span className="tts-liquidacion-label">{t('Tipo de liquidación')}:</span>
            <span className="tts-liquidacion-value">{settlementType.text}</span>
          </div>
        </div>
      </div>
    </div>,

    t('Generar liquidación.'),
  ];

  const setButtonDisability = (can: boolean) => {
    setCanGoNext(can);
  };

  const itemsMenu = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Liquidaciones directas'),
        iconProps: {
          iconName: 'TableComputed',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
      },
      {
        key: 'back',
        text: t('Volver'),
        onClick: () => navigate(`${routes.direct_settlement_list_aje}`),
        iconProps: { iconName: 'Back' },
        buttonStyles: defaultCommandBarButtonStyles,
      },
    ],
    [navigate],
  );

  const getDataFromCompanySelectorAssistant = (month: DateTime) => {
    setMonth(month);
  };

  const hasSettlementItems = () => {
    if (liquidacionInsumosList.length > 0) {
      return true;
    } else if (liquidacionInsumosList.length < 1) {
      return false;
    } else {
      return null;
    }
  };

  const Selectors = [
    <SettlementPeriod
      system={props.system}
      courier={props.courier}
      getDataFromCompanySelectorAssistant={getDataFromCompanySelectorAssistant}
      setButtonDisability={setButtonDisability}
      liquidacionInsumosList={liquidacionInsumosList}
      hasItems={hasSettlementItems()}
      month={month}
      lastSettlement={lastConfirmatedSettlement}
      setSettlementType={setSettlementType}
      settlementType={settlementType}
      agrupadoresCorporacionLiquidadora={agrupadoresCorporacionLiquidadora}
    />,
    <SettlementsTable
      system={props.system}
      courier={props.courier}
      changeBlockUI={props.changeBlockUI}
      items={liquidacionInsumosList}
      setItems={setLiquidacionInsumosList}
      from={month}
      settlementType={settlementType}
      user={props.user}
      setButtonDisability={setButtonDisability}
    />,
    <GenerateSettlement
      system={props.system}
      courier={props.courier}
      setButtonDisability={setButtonDisability}
      dataTable={liquidacionInsumosList}
      generateSettlement={generateSettlement}
      generatedSettlementResponse={generatedSettlementResponse}
      settlementType={settlementType}
      from={month}
      user={props.user}
    />,
  ];

  if (agrupadoresCorporacionLiquidadora.loading) {
    return <Loading title="Cargando" description="Por favor espere estamos procesando la información necesaria para completar su solicitud." />;
  }

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={''}
      items={itemsMenu}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="highligth"
      titleBody={t('Proyectos')}
    >
      <div className="padding-1">
        <div className="ms-Grid bg-white-h100" dir="ltr">
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12 mt-2 ml-1 defense-file-container-desc">
              <Text className="ml-10px defense-file-title d-block">{t('Asistente para la generación de liquidaciones de servicios (directas)')}</Text>
            </div>
          </div>

          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12 ml-1"></div>
          </div>
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12">
              <Wizard componentsTitlesArray={Titles} componentsDescriptions={Descriptions} componentsArray={Selectors} canGoNext={canGoNext} />
            </div>
          </div>
        </div>
      </div>
    </ViewWrapperWithCommandBar>
  );
};

export default DirectSettlementsViewAje;
